import { RefObject, ReactNode } from 'react';

import clsx from 'clsx';

import { Resizer } from './Resizer';
import { EllipsisWithTooltip, Tooltip } from '../../../tooltip';
import { ColumnModel, TableApi, TableComponents } from '../../types';

type Props = {
  columnModel: ColumnModel;
  api: RefObject<TableApi>;
  Sorter: Required<TableComponents>['Sorter'];
  SortIcon: Required<TableComponents>['SortIcon'];
  InfoIcon: Required<TableComponents>['InfoIcon'];
};

export const HeaderCell = ({
  columnModel,
  api,
  SortIcon,
  InfoIcon,
  Sorter,
}: Props) => {
  const { HeaderCellComponent, isFirstGroupColumn, isLastGroupColumn } =
    columnModel;

  const defaultClasses = clsx(
    `DS-Table-CellWrapper DS-Table-HeaderCellWrapper DS-Table-CellWrapper-${columnModel.id}`,
    isFirstGroupColumn && 'DS-Table-HeaderCellWrapper-firstGroupColumn',
    isLastGroupColumn && 'DS-Table-HeaderCellWrapper-lastGroupColumn'
  );

  if (HeaderCellComponent) {
    return <HeaderCellComponent className={defaultClasses} />;
  }

  const getHeaderContent = () => {
    if (columnModel.renderHeader) {
      return columnModel.renderHeader(api.current);
    }

    if (columnModel.header !== undefined) {
      if (
        columnModel.headerFullName === undefined &&
        ['string', 'number'].includes(typeof columnModel.header)
      ) {
        return (
          <EllipsisWithTooltip TooltipProps={{ placement: 'top' }}>
            {columnModel.header}
          </EllipsisWithTooltip>
        );
      }

      return (
        <Tooltip
          wrapIf={columnModel.headerFullName !== undefined}
          placement="top"
          title={columnModel.headerFullName}
          testId="header-full-name-tooltip-content"
        >
          <div
            className="DS-Table-HeaderTitle"
            data-testid="header-full-name-trigger"
          >
            {columnModel.header}
          </div>
        </Tooltip>
      );
    }

    return undefined;
  };

  const headerContent: ReactNode = getHeaderContent();

  return (
    <>
      <div
        className={clsx(
          defaultClasses,
          !!columnModel.infoTooltip && 'DS-Table-HeaderCell-hasInfoTooltip',
          !!columnModel.sortable && 'DS-Table-HeaderCell-sortable',
          !!columnModel.locked && 'DS-Table-lockedCell',
          !!columnModel.floating && 'DS-Table-floatingCell'
        )}
        data-column-id={columnModel.id}
      >
        <div className="DS-Table-Cell DS-Table-HeaderCell">
          {headerContent}
          {columnModel.infoTooltip && (
            <Tooltip
              arrow
              placement="top"
              testId={`table-header-info-tooltip-${columnModel.id}`}
              {...(columnModel.infoTooltipProps || {})}
              title={
                columnModel.infoTooltipProps?.title ?? columnModel.infoTooltip
              }
            >
              <InfoIcon className="DS-Table-InfoIcon Ds-Table-HeaderIcon-root" />
            </Tooltip>
          )}
          <Sorter
            api={api.current}
            columnModel={columnModel}
            SortIcon={SortIcon}
          />
        </div>
      </div>
      {columnModel.resizable && <Resizer column={columnModel} />}
    </>
  );
};
